import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ScrollToTop from '../components/ScrollToTop';
import { useSEO } from '../hooks/useSEO';
import { ArrowRight } from 'lucide-react';

const AboutPage: React.FC = () => {
  useSEO({
    title: 'About — The Story Behind Aura Traveler',
    description: 'Born from frustration with broken travel planning, Aura Traveler was built to give every traveler — and every agent — the tool the industry never had. Our origin story.',
    canonical: 'https://www.auratraveler.com/about',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'About', url: 'https://www.auratraveler.com/about' },
    ],
  });

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 blur-[120px] rounded-full -mr-64 -mt-64 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold/3 blur-[100px] rounded-full -ml-48 -mb-48 pointer-events-none" />
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/60 mb-8 block">
              Our Story
            </span>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl text-white tracking-tighter leading-[0.9] mb-8">
              Built from <span className="italic font-serif text-white/80">Frustration.</span><br />
              Designed for <span className="italic font-serif text-gold/80">Obsession.</span>
            </h1>
            <p className="font-serif text-xl sm:text-2xl text-white/50 italic max-w-2xl leading-relaxed mb-10">
              The best tools are born when someone refuses to accept that "good enough" is the standard.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-10 py-4 bg-gold text-black font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white transition-all duration-500"
            >
              Explore Membership
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-y border-white/5 relative">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">The Beginning</span>

            <div className="space-y-8 font-serif text-lg sm:text-xl text-white/70 leading-relaxed">
              <p>
                It started with a trip that should have been effortless.
              </p>
              <p>
                Days spent scrolling through dozens of tabs. Cross-referencing restaurant reviews against walking distances.
                Checking whether that rooftop bar with the sunset view was actually open on Tuesdays. Building a spreadsheet
                that somehow still felt incomplete. All of it — for a single weekend getaway.
              </p>
              <p>
                The irony wasn't lost on us: planning the trip was more exhausting than the trip itself.
              </p>
              <p>
                We'd worked with concierge teams before. Hired travel agents. The deliverables were always the same —
                a plain-text email with a few restaurant names, a hotel confirmation, and a generic document that could have been
                written for anyone. Nothing bespoke. Nothing that captured the <span className="text-white italic">feeling</span> of
                the journey we were trying to create.
              </p>
              <p className="text-white/90 text-xl sm:text-2xl border-l-2 border-gold/40 pl-8 py-2">
                We didn't just want an itinerary. We wanted a document that felt like opening a private travel magazine
                — one written exclusively for us.
              </p>
              <p>
                So we built it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Full-width travel image break */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2000&auto=format&fit=crop&fm=webp"
          alt="Aerial view of pristine coastline and turquoise waters"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-transparent to-charcoal" />
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-serif text-2xl md:text-3xl text-white italic text-center max-w-2xl px-8 drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
            "The best tools are invisible. They amplify your instinct, not replace it."
          </p>
        </div>
      </section>

      {/* The Gap */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24"
          >
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">The Gap</span>
              <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tighter leading-tight mb-8">
                A tool the industry <br /><span className="italic font-serif text-white/80">never had.</span>
              </h2>
              <div className="space-y-6 font-serif text-lg text-white/60 leading-relaxed">
                <p>
                  The more we looked, the clearer it became: this wasn't just our problem. Travel agents were spending
                  entire mornings building itineraries by hand. Concierge desks were handing guests the same recycled
                  recommendations. The everyday traveler — the person who actually <span className="text-white/80">cares</span> about
                  where they eat, what they see, how they feel — was left to figure it out alone.
                </p>
                <p>
                  The industry had tools for booking. Tools for flights. Tools for reviews.
                  But nothing for the <span className="text-white italic">design</span> of the experience itself.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              {[
                { stat: '16', unit: 'hrs', label: 'Average time spent planning a single trip', source: 'Priceline / Harris Poll' },
                { stat: '38', unit: '+', label: 'Websites visited before a traveler books', source: 'Expedia Group Research' },
                { stat: '90', unit: 'sec', label: 'Time Aura takes to generate a complete itinerary', source: 'Aura — measured generation time' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  className="p-8 border border-white/5 bg-white/[0.02] hover:border-gold/20 transition-all duration-500"
                >
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-display text-4xl text-gold tracking-tighter">{item.stat}</span>
                    <span className="font-serif text-lg text-gold/60 italic">{item.unit}</span>
                  </div>
                  <p className="font-sans text-[11px] uppercase tracking-[0.15em] text-white/70 mb-2">{item.label}</p>
                  <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-white/30">{item.source}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Founder — built by an agency operator (final copy + headshot to
          come from Lucas; the facts below are from the elevation brief) */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">Built by an Operator</span>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-8">
            Eighteen years of building brands.<br />
            <span className="font-serif italic font-medium text-gold">One tool we needed ourselves.</span>
          </h2>
          <p className="font-serif text-lg text-white/70 leading-relaxed mb-6 max-w-2xl">
            Aura Traveler is built by the founder of Fifty &amp; Five, a digital agency that has spent
            18 years producing work for more than 215 brands. It wasn&rsquo;t conceived in a pitch
            deck — it was built the way agencies build things for themselves: to eliminate the
            14-hour production grind behind every beautiful travel document.
          </p>
          <p className="font-serif text-lg text-white/70 leading-relaxed max-w-2xl">
            That operator DNA is why Aura obsesses over the details agencies get judged on —
            verified links, real venues, and output you&rsquo;d put your own logo on.
          </p>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">The Vision</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tighter leading-tight mb-12">
              Fast. Beautiful. <span className="italic font-serif text-gold/80">Intelligent.</span>
            </h2>

            <div className="space-y-8 font-serif text-lg sm:text-xl text-white/70 leading-relaxed">
              <p>
                Aura Traveler was built around a simple conviction: everyone deserves access to the kind of travel
                planning that was previously reserved for the ultra-wealthy with dedicated staff. And the professionals
                who <span className="text-white/80">do</span> this work for a living deserve a tool that respects their time.
              </p>
              <p>
                The engine processes hundreds of variables in seconds — your pace, your palate, your aesthetic, your budget,
                the season, local events, walking distances, opening hours, the quality of light at sunset. It does the
                research that used to take days, and it presents the result in a format that belongs on a coffee table,
                not buried in an email thread.
              </p>
              <p>
                But we didn't stop at itineraries.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Travel image break */}
      <section className="relative h-[35vh] md:h-[45vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?q=80&w=2000&auto=format&fit=crop&fm=webp"
          alt="Sunset over a luxury villa with infinity pool overlooking the ocean"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 via-transparent to-charcoal" />
      </section>

      {/* Beyond Itineraries */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">Beyond the Itinerary</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tighter leading-tight mb-16">
              The journey doesn't end <br /><span className="italic font-serif text-white/80">at the itinerary.</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Social Studio */}
              <div className="p-8 md:p-12 border border-white/5 bg-white/[0.02] hover:border-gold/20 transition-all duration-500 group">
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-6 block">Aura Social Studio</span>
                <h3 className="font-display text-2xl text-white mb-6 tracking-tight group-hover:text-gold transition-colors duration-500">
                  Share the Experience
                </h3>
                <p className="font-serif text-base text-white/60 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
                  Travel is meant to be shared. The Social Studio generates cinematic vertical reels, editorial postcards,
                  and shareable content that captures the essence of your journey — not just a photo, but a feeling.
                  Because the way you share your travels says as much about you as the places you visit.
                </p>
              </div>

              {/* AI Travel Assistant */}
              <div className="p-8 md:p-12 border border-white/5 bg-white/[0.02] hover:border-gold/20 transition-all duration-500 group">
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-6 block">AI Assistant</span>
                <h3 className="font-display text-2xl text-white mb-6 tracking-tight group-hover:text-gold transition-colors duration-500">
                  Ask Aura Anything.
                </h3>
                <p className="font-serif text-base text-white/60 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
                  Every itinerary includes an embedded AI assistant. Ask about restaurant swaps, logistics,
                  hidden gems, or local customs — and get answers anchored to your specific trip.
                  It's not a generic search engine — it's an intelligence layer that already knows your itinerary.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Aura */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/[0.02] to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-10 block">The Core Principle</span>
            <h2 className="font-display text-4xl sm:text-6xl md:text-7xl text-white tracking-tighter leading-[0.9] mb-10">
              Everything is built <br />around your <span className="italic font-serif text-gold">Aura.</span>
            </h2>
            <p className="font-serif text-xl text-white/60 italic max-w-2xl mx-auto leading-relaxed mb-6">
              Your aura isn't just a preference profile — it's the invisible thread that connects every recommendation,
              every restaurant, every hidden alleyway, every moment of your journey. The engine doesn't just know
              <span className="text-white/80"> where</span> you want to go. It understands <span className="text-white/80">who you are</span> when
              you get there.
            </p>
            <p className="font-serif text-lg text-white/40 italic max-w-xl mx-auto leading-relaxed">
              That's the difference between an itinerary and an experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 md:py-48 px-4 sm:px-6 md:px-8 lg:px-16 bg-gradient-to-b from-charcoal to-[#000] text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative z-10"
        >
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl text-white mb-6 tracking-tighter">
            Ready to experience it?
          </h2>
          <p className="font-serif text-lg text-white/40 italic mb-12 max-w-lg mx-auto">
            See what the Aura Engine can do — for you, your agency, or your guests.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-3 px-12 py-5 bg-gold text-black font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white transition-all duration-500"
            >
              Explore Membership <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/for-agencies"
              className="inline-flex items-center justify-center gap-3 px-12 py-5 border border-white/10 text-white font-sans text-[10px] uppercase tracking-[0.3em] hover:border-gold/50 hover:text-gold transition-all duration-500"
            >
              Agency Solutions
            </Link>
          </div>
        </motion.div>
      </section>

      </main>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
};

export default AboutPage;
